import { getFallbackTimeoutMs } from '../Services/transferConfig';
import { useEffect, useState } from 'react';
import { Box, Button, SvgIcon, Typography } from '@mui/material';
import { Signaling } from '../Services/Signaling';
import firebaseService from '../Services/firebaseService';
import { useI18n } from '../i18n/I18nProvider';

/** Intentionally English until the new flow has been reviewed. */
export default function TransferFallback({
    signaling,
}: {
    signaling: Signaling | null;
}) {
    const { t } = useI18n();
    const [reason, setReason] = useState<string | null>(null);
    const [enabled, setEnabled] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [waiting, setWaiting] = useState(false);
    useEffect(() => {
        setWaiting(false);
    }, [signaling, reason]);
    useEffect(() => {
        if (!waiting) return;
        const timer = setTimeout(
            () => setWaiting(false),
            getFallbackTimeoutMs(),
        );
        return () => clearTimeout(timer);
    }, [waiting]);
    useEffect(() => {
        setReason(null);
        setEnabled(false);
        setBusy(false);
        setError('');
        if (!signaling) return;
        const prompt = signaling.fallbackReason.subscribe((value) => {
            setReason(value);
            if (value)
                void firebaseService.logEvent('transfer_fallback_offered', {
                    session_id: signaling.id,
                    reason: value,
                    role: signaling.localRole,
                    simulated: signaling.emulateSlow,
                });
        });
        const mode = signaling.serverState.subscribe((state) =>
            setEnabled(state.enabled === true),
        );
        return () => {
            prompt.unsubscribe();
            mode.unsubscribe();
        };
    }, [signaling]);
    if (!signaling || !reason || enabled || waiting) return null;
    const select = async () => {
        setBusy(true);
        setError('');
        try {
            await signaling.selectServer();
            void firebaseService.logEvent('transfer_fallback_accepted', {
                session_id: signaling.id,
                reason,
                role: signaling.localRole,
                simulated: signaling.emulateSlow,
            });
        } catch {
            setError(t('transfer.switchError'));
        } finally {
            setBusy(false);
        }
    };
    return (
        <Box
            sx={{
                p: 2.5,
                mt: 2,
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '12px',
                textAlign: 'left',
                color: '#0f172a',
            }}
        >
            <Box role="status" aria-live="polite">
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    {/* Google Material Symbols: release_alert (outlined). */}
                    <SvgIcon
                        viewBox="0 -960 960 960"
                        aria-hidden="true"
                        sx={{ fontSize: 22, flexShrink: 0, color: '#b91c1c' }}
                    >
                        <path d="m344-60-76-128-144-32 14-148-98-112 98-112-14-148 144-32 76-128 136 58 136-58 76 128 144 32-14 148 98 112-98 112 14 148-144 32-76 128-136-58-136 58Zm34-102 102-44 104 44 56-96 110-26-10-112 74-84-74-86 10-112-110-24-58-96-102 44-104-44-56 96-110 24 10 112-74 86 74 84-10 114 110 24 58 96Zm102-318Zm0 200q17 0 28.5-11.5T520-320q0-17-11.5-28.5T480-360q-17 0-28.5 11.5T440-320q0 17 11.5 28.5T480-280Zm-40-160h80v-240h-80v240Z" />
                    </SvgIcon>
                    <Typography
                        sx={{
                            fontSize: '1rem',
                            fontWeight: 700,
                            lineHeight: 1.4,
                        }}
                    >
                        {reason === 'slow_connection'
                            ? t('transfer.slowConnection')
                            : t('transfer.connectionFailed')}
                    </Typography>
                </Box>
                <Typography
                    sx={{
                        mt: 0.75,
                        fontSize: '0.875rem',
                        lineHeight: 1.6,
                        color: '#475569',
                    }}
                >
                    {t('transfer.serverDescription')}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mt: 2 }}>
                <Button
                    variant="contained"
                    disableElevation
                    onClick={select}
                    disabled={busy}
                    sx={{
                        minHeight: 44,
                        px: 3,
                        borderRadius: '9999px',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 800,
                        backgroundColor: '#2563eb',
                        '&:hover': { backgroundColor: '#1d4ed8' },
                    }}
                >
                    {busy ? t('transfer.connecting') : t('transfer.useServer')}
                </Button>
                <Button
                    variant="outlined"
                    disabled={busy}
                    onClick={() => {
                        setWaiting(true);
                        void firebaseService.logEvent(
                            'transfer_fallback_wait',
                            {
                                session_id: signaling.id,
                                reason,
                                role: signaling.localRole,
                            },
                        );
                    }}
                    sx={{
                        minHeight: 44,
                        px: 3,
                        borderRadius: '9999px',
                        textTransform: 'none',
                        fontSize: '0.875rem',
                        fontWeight: 700,
                        color: '#2563eb',
                        borderColor: '#2563eb',
                        backgroundColor: '#ffffff',
                        '&:hover': {
                            backgroundColor: '#eff6ff',
                            borderColor: '#1d4ed8',
                        },
                    }}
                >
                    {t('transfer.keepWaiting')}
                </Button>
            </Box>
            <Typography
                sx={{
                    mt: 1.25,
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    color: '#64748b',
                }}
            >
                {t('transfer.temporaryStorage')}
            </Typography>
            {error && (
                <Typography
                    role="alert"
                    color="error"
                    sx={{ mt: 1, fontSize: '0.875rem', lineHeight: 1.5 }}
                >
                    {error}
                </Typography>
            )}
        </Box>
    );
}
